import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Save, Send, X, Euro, Percent, 
  FileText, Image, Upload, Package, BookOpen, Check, Download, FileCheck
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { parseItalianNumber, formatItalianNumber, normalizeNumericInput } from '../lib/numericUtils';

const FurnitureQuoteForm = ({ 
  isOpen, 
  onClose, 
  projectId,
  leadId,
  leadName,
  clientName,
  existingQuote = null,
  onSaved 
}) => {
  const { user } = useAuth();
  
  // Form state
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // V2: Tracking quote effettivo (parte da existingQuote, aggiornato dopo Salva keep-open)
  // Permette di fare PUT successivi sullo stesso id senza chiudere la modale
  const [currentQuote, setCurrentQuote] = useState(existingQuote);
  // V2: Tracking modifiche non salvate (per conferma in Annulla)
  const [dirty, setDirty] = useState(false);
  
  // V2.1: Promozioni ufficiali da Impostazioni
  const [availablePromotions, setAvailablePromotions] = useState([]);
  const [selectedPromotionId, setSelectedPromotionId] = useState(null);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  
  // V2.1: Chiusura commerciale
  const [closingType, setClosingType] = useState('none'); // none, fixed, final_price
  const [closingDiscountAmount, setClosingDiscountAmount] = useState(0);
  const [closingFinalPrice, setClosingFinalPrice] = useState(0);
  const [closingNotes, setClosingNotes] = useState('');
  
  // V2: Acconto/Saldo configurabili
  const [accontoPercentage, setAccontoPercentage] = useState(50);
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { index, field }
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);
  
  // Voci frequenti
  const [frequentItems, setFrequentItems] = useState([]);
  const [showFrequentItems, setShowFrequentItems] = useState(false);
  const [newFrequentItem, setNewFrequentItem] = useState({ description: '', default_unit_price: 0, category: '' });
  const [showSaveFrequent, setShowSaveFrequent] = useState(false);
  const [itemToSaveAsFrequent, setItemToSaveAsFrequent] = useState(null);

  // Focus input quando si entra in edit mode
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Inizializza form
  useEffect(() => {
    if (isOpen) {
      // V2: Sincronizza stato locale quote + reset dirty
      setCurrentQuote(existingQuote);
      setDirty(false);
      loadAvailablePromotions();
      if (existingQuote) {
        setTitle(existingQuote.title || '');
        setNotes(existingQuote.notes || '');
        setItems(existingQuote.items || []);
        setAttachments(existingQuote.attachments || []);
        // V2.1: Carica promozione ufficiale
        setSelectedPromotionId(existingQuote.promotion_id || null);
        // V2.1: Carica chiusura commerciale
        setClosingType(existingQuote.closing_discount_type || 'none');
        setClosingDiscountAmount(existingQuote.closing_discount_amount || 0);
        setClosingFinalPrice(existingQuote.closing_final_price || 0);
        setClosingNotes(existingQuote.closing_notes || '');
        // V2: Carica acconto
        setAccontoPercentage(existingQuote.acconto_percentage || 50);
      } else {
        // Titolo precompilato con nome cliente/lead
        const name = leadName || clientName || '';
        setTitle(name ? `Preventivo Arredi - ${name}` : '');
        setNotes('');
        setItems([createEmptyItem()]);
        setAttachments([]);
        // V2.1: Reset valori default
        setSelectedPromotionId(null);
        setClosingType('none');
        setClosingDiscountAmount(0);
        setClosingFinalPrice(0);
        setClosingNotes('');
        setAccontoPercentage(50);
      }
      loadFrequentItems();
      setEditingCell(null);
    }
  }, [isOpen, existingQuote, leadName, clientName]);

  // Carica promozioni attive da Impostazioni
  const loadAvailablePromotions = async () => {
    setLoadingPromotions(true);
    try {
      const response = await fetch(`/api/furniture-quotes-promotions`, {
        withCredentials: true
      });
      if (response.ok) {
        const promos = await response.json();
        setAvailablePromotions(promos);
      }
    } catch (e) {
      console.error('Error loading promotions:', e);
    } finally {
      setLoadingPromotions(false);
    }
  };

  const loadFrequentItems = async () => {
    try {
      const response = await fetch(`/api/furniture-frequent-items`, {
        withCredentials: true
      });
      if (response.ok) {
        setFrequentItems(await response.json());
      }
    } catch (e) {
      console.error('Error loading frequent items:', e);
    }
  };

  const createEmptyItem = () => ({
    id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    description: '',
    quantity: 1,
    unit_measure: 'cad',  // V2: Unità di misura default
    unit_price: 0,
    discount_percentage: 0,
    notes: ''
  });

  // Inline editing handlers
  const startEditing = (index, field, currentValue) => {
    setEditingCell({ index, field });
    setEditValue(currentValue?.toString() || '');
  };

  const confirmEdit = () => {
    if (!editingCell) return;
    
    const { index, field } = editingCell;
    let value = editValue;
    
    // Parse value based on field type using Italian number parsing
    if (field === 'quantity') {
      const parsed = parseItalianNumber(value, null);
      value = parsed !== null ? Math.max(1, Math.round(parsed)) : 1;
    } else if (field === 'unit_price') {
      const parsed = parseItalianNumber(value, null);
      value = parsed !== null ? Math.max(0, parsed) : 0;
    } else if (field === 'discount_percentage') {
      const parsed = parseItalianNumber(value, null);
      value = parsed !== null ? Math.min(100, Math.max(0, parsed)) : 0;
    }
    
    handleItemChange(index, field, value);
    setEditingCell(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      confirmEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      confirmEdit();
      // Move to next cell
      if (editingCell) {
        const fields = ['quantity', 'unit_price', 'discount_percentage'];
        const currentFieldIndex = fields.indexOf(editingCell.field);
        if (currentFieldIndex === -1) return;
        const nextFieldIndex = (currentFieldIndex + 1) % fields.length;
        const nextItemIndex = nextFieldIndex === 0 ? editingCell.index + 1 : editingCell.index;
        
        if (nextItemIndex < items.length) {
          setTimeout(() => {
            startEditing(nextItemIndex, fields[nextFieldIndex], items[nextItemIndex][fields[nextFieldIndex]]);
          }, 50);
        }
      }
    }
  };

  // Calcoli V2.1: Con promozione ufficiale + chiusura commerciale
  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discount = subtotal * ((item.discount_percentage || 0) / 100);
    return subtotal - discount;
  };

  // STEP 1: Subtotale articoli
  const subtotalGross = items.reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  const totalDiscount = items.reduce((sum, item) => {
    const itemSubtotal = (item.quantity || 0) * (item.unit_price || 0);
    return sum + itemSubtotal * ((item.discount_percentage || 0) / 100);
  }, 0);
  const subtotalAfterItems = subtotalGross - totalDiscount;  // Dopo sconti riga
  
  // STEP 2: Promozione ufficiale da Impostazioni
  const selectedPromotion = availablePromotions.find(p => p.id === selectedPromotionId);
  let promotionDiscountAmount = 0;
  let promotionPercentage = 0;
  let promotionName = '';
  
  if (selectedPromotion && selectedPromotion.discount_percentage > 0) {
    promotionPercentage = selectedPromotion.discount_percentage;
    promotionName = selectedPromotion.name;
    promotionDiscountAmount = subtotalAfterItems * (promotionPercentage / 100);
  }
  
  // STEP 3: Totale dopo promozione (imponibile)
  const totalAfterPromotion = subtotalAfterItems - promotionDiscountAmount;
  
  // STEP 3b: Totale dopo promozione IVA INCLUSA (per confronto con prezzo finale)
  const totalAfterPromotionGross = totalAfterPromotion * 1.22;
  
  // STEP 4: Chiusura commerciale
  let closingDiscountCalculated = 0;  // Sconto imponibile
  let closingPercentageCalculated = 0;
  let closingDiscountGross = 0;  // Sconto lordo (IVA inclusa)
  let closingFinalPriceNet = 0;  // Imponibile scorporato
  
  if (closingType === 'fixed' && closingDiscountAmount > 0) {
    // Extra sconto fisso: importo imponibile
    closingDiscountCalculated = Math.min(closingDiscountAmount, totalAfterPromotion);
  } else if (closingType === 'final_price' && closingFinalPrice > 0) {
    // V2.1 FIX: Prezzo finale concordato IVA INCLUSA
    // closingFinalPrice è ora IVA INCLUSA (lordo)
    
    if (closingFinalPrice > totalAfterPromotionGross) {
      // Validazione: prezzo finale non può superare totale lordo
      closingDiscountCalculated = 0;
    } else {
      // Scorporo IVA dal prezzo finale
      closingFinalPriceNet = closingFinalPrice / 1.22;
      
      // Sconto imponibile = totale dopo promo (imponibile) - prezzo finale (imponibile)
      closingDiscountCalculated = totalAfterPromotion - closingFinalPriceNet;
      
      // Sconto lordo per visualizzazione
      closingDiscountGross = totalAfterPromotionGross - closingFinalPrice;
      
      // Percentuale reale calcolata sul lordo
      if (totalAfterPromotionGross > 0) {
        closingPercentageCalculated = (closingDiscountGross / totalAfterPromotionGross) * 100;
      }
    }
  }
  
  // STEP 5-6: Imponibile, IVA e totale finale
  let taxableAmount, ivaAmount, grandTotal;
  
  if (closingType === 'final_price' && closingFinalPrice > 0 && closingFinalPrice <= totalAfterPromotionGross) {
    // FIX ARROTONDAMENTO: Quando l'utente inserisce un prezzo finale IVA inclusa,
    // grandTotal DEVE essere ESATTAMENTE uguale a quel valore.
    grandTotal = closingFinalPrice;  // ESATTO, nessun arrotondamento
    taxableAmount = Math.round((closingFinalPrice / 1.22) * 100) / 100;
    ivaAmount = Math.round((closingFinalPrice - taxableAmount) * 100) / 100;
  } else {
    // Caso standard (nessuna chiusura o extra sconto fisso)
    taxableAmount = totalAfterPromotion - closingDiscountCalculated;
    ivaAmount = taxableAmount * 0.22;
    grandTotal = taxableAmount + ivaAmount;
  }
  
  // STEP 7: Acconto/Saldo configurabili
  const saldoPercentage = 100 - accontoPercentage;
  const accontoAmount = Math.round((grandTotal * (accontoPercentage / 100)) * 100) / 100;
  const saldoAmount = Math.round((grandTotal - accontoAmount) * 100) / 100;  // Saldo = totale - acconto (garantisce somma esatta)

  // Handlers
  const handleAddItem = () => {
    setItems([...items, createEmptyItem()]);
    setDirty(true);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      setDirty(true);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    setDirty(true);
  };

  const handleAddFrequentItem = (frequent) => {
    const newItem = {
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      description: frequent.description,
      quantity: frequent.default_quantity || 1,
      unit_price: frequent.default_unit_price || 0,
      discount_percentage: 0,
      notes: ''
    };
    setItems([...items, newItem]);
    setShowFrequentItems(false);
    setDirty(true);
  };

  // V2: Intercetta la richiesta di chiusura modale per chiedere conferma se dirty
  const handleCloseRequest = () => {
    if (saving) return;
    if (dirty) {
      const ok = window.confirm('Ci sono modifiche non salvate. Vuoi uscire senza salvare?');
      if (!ok) return;
    }
    onClose();
  };

  const handleSaveAsFrequent = async () => {
    if (!itemToSaveAsFrequent) return;
    
    try {
      const response = await fetch(`/api/furniture-frequent-items`, {
        method: 'POST',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          description: itemToSaveAsFrequent.description,
          default_unit_price: itemToSaveAsFrequent.unit_price,
          default_quantity: itemToSaveAsFrequent.quantity,
          category: newFrequentItem.category
        })
      });
      
      if (response.ok) {
        toast.success('Voce salvata nelle voci frequenti');
        loadFrequentItems();
        setShowSaveFrequent(false);
        setItemToSaveAsFrequent(null);
      }
    } catch (e) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Tipo file non supportato: ${file.name}`);
        continue;
      }
      
      // Per ora salviamo come base64 (in futuro con Object Storage)
      const reader = new FileReader();
      reader.onload = () => {
        const newAttachment = {
          id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          filename: file.name,
          url: reader.result,
          file_type: file.type.startsWith('image/') ? 'image' : 'pdf',
          uploaded_at: new Date().toISOString()
        };
        setAttachments(prev => [...prev, newAttachment]);
        setDirty(true);
      };
      reader.readAsDataURL(file);
    }
    
    e.target.value = '';
  };

  const handleRemoveAttachment = (id) => {
    setAttachments(attachments.filter(a => a.id !== id));
    setDirty(true);
  };

  const handleSave = async (presentToClient = false, keepOpen = false) => {
    // V2: Guard anti doppio-salvataggio (oltre al disabled={saving} del bottone)
    if (saving) return;
    
    if (!title.trim()) {
      toast.error('Inserisci un titolo per il preventivo');
      return;
    }
    
    if (items.length === 0 || !items.some(i => i.description.trim())) {
      toast.error('Aggiungi almeno una voce al preventivo');
      return;
    }
    
    setSaving(true);
    
    try {
      const validItems = items.filter(i => i.description.trim()).map(item => ({
        ...item,
        id: item.id.startsWith('new-') ? undefined : item.id,
        unit_measure: item.unit_measure || 'cad'  // V2: Assicura unit_measure
      }));
      
      const payload = {
        project_id: projectId,
        title,
        notes,
        items: validItems,
        attachments,
        status: presentToClient ? 'presentato' : 'bozza',
        // V2.1: Promozione ufficiale da Impostazioni
        promotion_id: selectedPromotionId || null,
        // V2.1: Chiusura commerciale
        closing_discount_type: closingType,
        closing_discount_amount: closingType === 'fixed' ? closingDiscountAmount : 0,
        closing_final_price: closingType === 'final_price' ? closingFinalPrice : 0,
        closing_notes: closingNotes || null,
        // V2: Acconto configurabile
        acconto_percentage: accontoPercentage
      };
      
      // V2: Usa currentQuote (state locale) per supportare PUT successivi nella stessa sessione
      const editingId = currentQuote?.id;
      let url;
      if (editingId) {
        url = `/api/furniture-quotes/${editingId}`;
      } else if (leadId) {
        url = `/api/leads/${leadId}/furniture-quotes`;
      } else if (projectId) {
        url = `/api/projects/${projectId}/furniture-quotes`;
      } else {
        toast.error('Devi selezionare un Lead o un Progetto');
        setSaving(false);
        return;
      }
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        // Leggi body UNA SOLA VOLTA per evitare "Body is disturbed or locked"
        let errorMessage = 'Errore nel salvataggio';
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.detail || errorMessage;
            } catch {
              // Response non è JSON, usa il testo se breve
              if (errorText.length < 200) {
                errorMessage = errorText;
              }
            }
          }
        } catch {
          // Errore nel leggere il body - usa messaggio default
        }
        toast.error(errorMessage);
        setSaving(false);
        return;
      }
      
      const savedQuote = await response.json();
      
      // V2: Aggiorna stato locale + reset dirty (permette ulteriori PUT)
      setCurrentQuote(savedQuote);
      setDirty(false);
      
      if (keepOpen) {
        // Salva e resta nella modale
        toast.success('Preventivo salvato correttamente');
        onSaved?.(savedQuote, false);
      } else {
        // Salva e chiudi (o Presenta al Cliente)
        toast.success(editingId 
          ? 'Preventivo aggiornato' 
          : (presentToClient ? 'Preventivo presentato al cliente' : 'Preventivo salvato come bozza')
        );
        onSaved?.(savedQuote, true);
        onClose();
      }
      
    } catch (error) {
      // In caso di errore, lascia la modale aperta e dirty inalterato
      toast.error(error.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Download PDF - supporta modalità summary (cliente) e detailed (completo)
  const handleDownloadPDF = async (mode = 'detailed') => {
    if (!currentQuote?.id) {
      toast.error('Salva prima il preventivo per generare il PDF');
      return;
    }
    
    const modeLabel = mode === 'summary' ? 'cliente' : 'completo';
    const loadingToastId = toast.loading(`Generazione PDF ${modeLabel}...`);
    
    try {
      const response = await fetch(
        `/api/furniture-quotes/${currentQuote.id}/pdf?mode=${mode}`,
        {
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (!response.ok) {
        // Gestione errore sicura - leggi body UNA SOLA VOLTA
        let errorMessage = 'Errore nella generazione del PDF';
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.detail || errorMessage;
            } catch {
              // Response non è JSON, usa il testo se breve
              if (errorText.length < 200) {
                errorMessage = errorText;
              }
            }
          }
        } catch {
          // Errore nel leggere il body - usa messaggio default
        }
        toast.dismiss(loadingToastId);
        toast.error(errorMessage);
        return;
      }
      
      // Download del file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = mode === 'summary' ? '_cliente' : '_completo';
      a.download = `Preventivo_Arredi_${currentQuote.quote_number?.replace('/', '-') || 'download'}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss(loadingToastId);
      toast.success(`PDF ${modeLabel} scaricato`);
    } catch (error) {
      toast.dismiss(loadingToastId);
      toast.error(error.message || 'Errore download PDF');
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCloseRequest(); }}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="w-6 h-6 text-purple-600" />
            {existingQuote ? 'Modifica Preventivo Arredi' : 'Nuovo Preventivo Arredi'}
          </DialogTitle>
          <DialogDescription>
            Compila i dati del preventivo. Le voci possono essere aggiunte manualmente o dalle voci frequenti.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Titolo Preventivo *</Label>
              <Input
                value={title}
                onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                placeholder="Es: Cucina SCAVOLINI Evolution"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Note Generali</Label>
              <Textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
                placeholder="Specifiche, tempi di consegna, condizioni..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          {/* Voci Preventivo */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Voci Preventivo</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFrequentItems(true)}
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                >
                  <BookOpen className="w-4 h-4 mr-1" />
                  Voci Frequenti
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddItem}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi Voce
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                  data-testid={`furniture-item-card-${index}`}
                >
                  {/* Riga 1: Descrizione full-width (textarea autosize) */}
                  <div className="mb-3">
                    <Label className="text-xs font-medium text-gray-600 mb-1 block">
                      Descrizione voce
                    </Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => {
                        handleItemChange(index, 'description', e.target.value);
                        // autosize: cresce con il contenuto, max ~6 righe poi scroll interno
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                      }}
                      ref={(el) => {
                        // imposta altezza corretta al mount (per descrizioni esistenti lunghe)
                        if (el) {
                          el.style.height = 'auto';
                          el.style.height = Math.min(el.scrollHeight, 160) + 'px';
                        }
                      }}
                      rows={2}
                      placeholder="Es: Cucina così composta: colonna frigorifero..."
                      className="w-full text-sm resize-none"
                      style={{ minHeight: '56px', maxHeight: '160px', overflowY: 'auto' }}
                      data-testid={`item-description-${item.id}`}
                    />
                  </div>

                  {/* Riga 2: Q.tà, U.M., Prezzo, Sconto, Totale, Azioni */}
                  <div className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center">
                    {/* Quantità */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <Label className="text-xs text-gray-500 w-10 shrink-0">Q.tà</Label>
                      {editingCell?.index === index && editingCell?.field === 'quantity' ? (
                        <Input
                          ref={inputRef}
                          type="text"
                          inputMode="decimal"
                          value={editValue}
                          onChange={(e) => setEditValue(normalizeNumericInput(e.target.value))}
                          onKeyDown={handleKeyDown}
                          onBlur={confirmEdit}
                          className="h-8 text-sm text-center flex-1"
                          data-testid={`item-quantity-edit-${item.id}`}
                        />
                      ) : (
                        <div
                          onClick={() => startEditing(index, 'quantity', item.quantity)}
                          className="cursor-pointer px-2 py-1 rounded hover:bg-purple-100 text-center flex-1 min-h-[28px]"
                          title="Clicca per modificare"
                        >
                          {item.quantity}
                        </div>
                      )}
                    </div>

                    {/* Unità di misura */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <Label className="text-xs text-gray-500 w-10 shrink-0">U.M.</Label>
                      <Select
                        value={item.unit_measure || 'cad'}
                        onValueChange={(v) => handleItemChange(index, 'unit_measure', v)}
                      >
                        <SelectTrigger className="h-8 text-xs flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cad">cad</SelectItem>
                          <SelectItem value="pz">pz</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="mq">mq</SelectItem>
                          <SelectItem value="corpo">corpo</SelectItem>
                          <SelectItem value="forfait">forfait</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Prezzo */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <Label className="text-xs text-gray-500 w-12 shrink-0">Prezzo</Label>
                      {editingCell?.index === index && editingCell?.field === 'unit_price' ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Euro className="w-3 h-3 text-gray-400 shrink-0" />
                          <Input
                            ref={inputRef}
                            type="text"
                            inputMode="decimal"
                            value={editValue}
                            onChange={(e) => setEditValue(normalizeNumericInput(e.target.value))}
                            onKeyDown={handleKeyDown}
                            onBlur={confirmEdit}
                            className="h-8 text-sm text-right flex-1"
                            data-testid={`item-price-edit-${item.id}`}
                          />
                        </div>
                      ) : (
                        <div
                          onClick={() => startEditing(index, 'unit_price', item.unit_price)}
                          className="cursor-pointer px-2 py-1 rounded hover:bg-purple-100 text-right flex-1 min-h-[28px]"
                          title="Clicca per modificare"
                        >
                          {formatCurrency(item.unit_price)}
                        </div>
                      )}
                    </div>

                    {/* Sconto */}
                    <div className="md:col-span-2 flex items-center gap-2">
                      <Label className="text-xs text-gray-500 w-12 shrink-0">Sconto</Label>
                      {editingCell?.index === index && editingCell?.field === 'discount_percentage' ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Input
                            ref={inputRef}
                            type="text"
                            inputMode="decimal"
                            value={editValue}
                            onChange={(e) => setEditValue(normalizeNumericInput(e.target.value))}
                            onKeyDown={handleKeyDown}
                            onBlur={confirmEdit}
                            className="h-8 text-sm text-center flex-1"
                            data-testid={`item-discount-edit-${item.id}`}
                          />
                          <Percent className="w-3 h-3 text-gray-400 shrink-0" />
                        </div>
                      ) : (
                        <div
                          onClick={() => startEditing(index, 'discount_percentage', item.discount_percentage)}
                          className="cursor-pointer px-2 py-1 rounded hover:bg-purple-100 text-center flex-1 min-h-[28px]"
                          title="Clicca per modificare"
                        >
                          {item.discount_percentage > 0 ? (
                            <span className="text-red-600">-{item.discount_percentage}%</span>
                          ) : (
                            <span className="text-gray-400">0%</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Totale */}
                    <div className="md:col-span-2 flex items-center gap-2 justify-end">
                      <Label className="text-xs text-gray-500 shrink-0">Totale</Label>
                      <span
                        className="font-semibold text-[#FF7819] text-right"
                        data-testid={`item-total-${item.id}`}
                      >
                        {formatCurrency(calculateItemTotal(item))}
                      </span>
                    </div>

                    {/* Azioni */}
                    <div className="md:col-span-2 flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setItemToSaveAsFrequent(item);
                          setShowSaveFrequent(true);
                        }}
                        className="text-purple-600 hover:bg-purple-50 h-8 w-8 p-0"
                        title="Salva come voce frequente"
                        data-testid={`item-save-frequent-${item.id}`}
                      >
                        <BookOpen className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                        disabled={items.length === 1}
                        title="Rimuovi voce"
                        data-testid={`item-remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Helper text */}
              <p className="text-xs text-gray-500 italic">
                La descrizione è sempre modificabile. Clicca su quantità, prezzo o sconto per editarli. Premi Invio per confermare, Esc per annullare, Tab per passare al campo successivo.
              </p>
            </div>
          </div>

          {/* V2.1: Promozione Ufficiale (da Impostazioni) */}
          <Card className="border-2 border-dashed border-green-200 bg-green-50/30">
            <CardContent className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-green-700">
                <Percent className="w-4 h-4" />
                Promozione Ufficiale
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Seleziona promozione da Impostazioni</Label>
                  <Select
                    value={selectedPromotionId || "none"}
                    onValueChange={(v) => { setSelectedPromotionId(v === "none" ? null : v); setDirty(true); }}
                    disabled={loadingPromotions}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={loadingPromotions ? "Caricamento..." : "Nessuna promozione"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna promozione</SelectItem>
                      {availablePromotions.map(promo => (
                        <SelectItem key={promo.id} value={promo.id}>
                          {promo.name} {promo.discount_percentage > 0 ? `(-${promo.discount_percentage}%)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedPromotion && (
                  <div className="bg-white rounded p-3 border border-green-200 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-green-700">{selectedPromotion.name}</span>
                      <span className="text-green-600 font-bold">-{selectedPromotion.discount_percentage}%</span>
                    </div>
                    {selectedPromotion.description && (
                      <p className="text-gray-500 text-xs mt-1">{selectedPromotion.description}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* V2.1: Chiusura Commerciale */}
          <Card className="border-2 border-dashed border-orange-200 bg-orange-50/30">
            <CardContent className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-700">
                <Euro className="w-4 h-4" />
                Chiusura Commerciale
              </h4>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="closingType"
                      value="none"
                      checked={closingType === 'none'}
                      onChange={() => { setClosingType('none'); setDirty(true); }}
                      className="text-orange-500"
                    />
                    <span className="text-sm">Nessuna</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="closingType"
                      value="fixed"
                      checked={closingType === 'fixed'}
                      onChange={() => { setClosingType('fixed'); setDirty(true); }}
                      className="text-orange-500"
                    />
                    <span className="text-sm">Extra sconto €</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="closingType"
                      value="final_price"
                      checked={closingType === 'final_price'}
                      onChange={() => { setClosingType('final_price'); setDirty(true); }}
                      className="text-orange-500"
                    />
                    <span className="text-sm">Prezzo finale concordato</span>
                  </label>
                </div>
                
                {closingType === 'fixed' && (
                  <div>
                    <Label className="text-xs text-gray-600">Extra sconto (€)</Label>
                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={closingDiscountAmount || ''}
                        onChange={(e) => { setClosingDiscountAmount(normalizeNumericInput(e.target.value)); setDirty(true); }}
                        onBlur={(e) => {
                          const parsed = parseItalianNumber(e.target.value, 0);
                          setClosingDiscountAmount(Math.max(0, parsed));
                        }}
                        className="h-9 w-40"
                        placeholder="0,00"
                        data-testid="closing-discount-amount"
                      />
                    </div>
                  </div>
                )}
                
                {closingType === 'final_price' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-600">Prezzo finale concordato IVA inclusa (€)</Label>
                    <p className="text-[10px] text-gray-400 -mt-1">
                      Inserisci il prezzo finito concordato con il cliente. Il sistema calcola automaticamente imponibile, IVA e sconto reale.
                    </p>
                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={closingFinalPrice || ''}
                        onChange={(e) => { setClosingFinalPrice(normalizeNumericInput(e.target.value)); setDirty(true); }}
                        onBlur={(e) => {
                          const parsed = parseItalianNumber(e.target.value, 0);
                          setClosingFinalPrice(Math.max(0, parsed));
                        }}
                        className="h-9 w-40"
                        placeholder={formatItalianNumber(totalAfterPromotionGross, 2)}
                        data-testid="closing-final-price"
                      />
                    </div>
                    {closingFinalPrice > 0 && closingFinalPrice > totalAfterPromotionGross && (
                      <p className="text-xs text-red-500 mt-1">
                        Il prezzo finale IVA inclusa non può essere maggiore di {formatCurrency(totalAfterPromotionGross)}
                      </p>
                    )}
                    {closingFinalPrice > 0 && closingFinalPrice <= totalAfterPromotionGross && (
                      <div className="mt-2 p-2 bg-orange-50 rounded-lg text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Imponibile calcolato:</span>
                          <span className="font-medium">{formatCurrency(closingFinalPriceNet)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">IVA 22%:</span>
                          <span className="font-medium">{formatCurrency(closingFinalPrice - closingFinalPriceNet)}</span>
                        </div>
                        <div className="flex justify-between text-orange-600">
                          <span>{closingDiscountGross < 10 ? 'Arrotondamento commerciale:' : 'Extra sconto:'}</span>
                          <span className="font-medium">-{formatCurrency(closingDiscountGross)}</span>
                        </div>
                        {closingDiscountGross >= 10 && (
                          <div className="flex justify-between text-orange-700 font-semibold border-t border-orange-200 pt-1">
                            <span>Sconto reale:</span>
                            <span>{closingPercentageCalculated.toFixed(2)}%</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {closingType !== 'none' && (
                  <div>
                    <Label className="text-xs text-gray-600">Note chiusura (opzionale)</Label>
                    <Input
                      value={closingNotes}
                      onChange={(e) => { setClosingNotes(e.target.value); setDirty(true); }}
                      placeholder="Es. trattativa telefonica del 15/05"
                      className="h-9"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* V2.1: Riepilogo Economico aggiornato */}
          <Card className="card-abitalo bg-gradient-to-r from-purple-50 to-white">
            <CardContent className="p-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Euro className="w-4 h-4 text-purple-600" />
                Riepilogo Economico
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-gray-600">Subtotale articoli:</span>
                <span className="text-right">{formatCurrency(subtotalGross)}</span>
                
                {totalDiscount > 0 && (
                  <>
                    <span className="text-gray-600">Sconti riga:</span>
                    <span className="text-right text-red-600">-{formatCurrency(totalDiscount)}</span>
                    
                    <span className="text-gray-600">Subtotale netto righe:</span>
                    <span className="text-right">{formatCurrency(subtotalAfterItems)}</span>
                  </>
                )}
                
                {promotionDiscountAmount > 0 && (
                  <>
                    <span className="text-green-600">
                      {promotionName} (-{promotionPercentage}%):
                    </span>
                    <span className="text-right text-green-600 font-medium">-{formatCurrency(promotionDiscountAmount)}</span>
                    
                    <span className="text-gray-600">Totale dopo promozione:</span>
                    <span className="text-right">{formatCurrency(totalAfterPromotion)}</span>
                  </>
                )}
                
                {closingDiscountCalculated > 0 && (
                  <>
                    <span className="text-orange-600">
                      {closingType === 'final_price' 
                        ? (closingDiscountGross < 10 
                            ? 'Arrotondamento commerciale'
                            : `Prezzo concordato (sconto ${closingPercentageCalculated.toFixed(2)}%)`)
                        : (closingDiscountGross < 10 
                            ? 'Arrotondamento commerciale'
                            : 'Extra sconto chiusura')}:
                    </span>
                    <span className="text-right text-orange-600 font-medium">-{formatCurrency(closingDiscountCalculated)}</span>
                  </>
                )}
                
                <span className="text-gray-600 border-t pt-2 font-medium">Imponibile finale:</span>
                <span className="text-right border-t pt-2 font-medium">{formatCurrency(taxableAmount)}</span>
                
                <span className="text-gray-600">IVA 22%:</span>
                <span className="text-right">{formatCurrency(ivaAmount)}</span>
                
                <span className="font-bold text-[#3C3C3C] border-t pt-2">TOTALE:</span>
                <span className="text-right font-bold text-[#FF7819] text-lg border-t pt-2">{formatCurrency(grandTotal)}</span>
              </div>
              
              {/* V2: Acconto/Saldo configurabili */}
              <div className="mt-4 pt-3 border-t">
                <div className="flex items-center gap-4 mb-2">
                  <Label className="text-xs text-gray-600">Acconto %:</Label>
                  <Select
                    value={String(accontoPercentage)}
                    onValueChange={(v) => { setAccontoPercentage(parseInt(v)); setDirty(true); }}
                  >
                    <SelectTrigger className="h-8 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30%</SelectItem>
                      <SelectItem value="40">40%</SelectItem>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="60">60%</SelectItem>
                      <SelectItem value="70">70%</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-gray-500">Saldo: {saldoPercentage}%</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 bg-gray-50 rounded p-2">
                  <span>Acconto ({accontoPercentage}%): <strong>{formatCurrency(accontoAmount)}</strong></span>
                  <span>Saldo ({saldoPercentage}%): <strong>{formatCurrency(saldoAmount)}</strong></span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Allegati */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Allegati (PDF o Immagini)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {attachments.map((att) => (
                <div 
                  key={att.id} 
                  className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 text-sm"
                >
                  {att.file_type === 'pdf' ? (
                    <FileText className="w-4 h-4 text-red-500" />
                  ) : (
                    <Image className="w-4 h-4 text-blue-500" />
                  )}
                  <span className="truncate max-w-[150px]">{att.filename}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAttachment(att.id)}
                    className="p-1 h-auto"
                  >
                    <X className="w-3 h-3 text-gray-500" />
                  </Button>
                </div>
              ))}
            </div>
            <label className="cursor-pointer">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                <span className="text-sm text-gray-500">Clicca per caricare PDF o immagini</span>
              </div>
              <input
                type="file"
                accept=".pdf,image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={handleCloseRequest} disabled={saving} data-testid="furniture-quote-cancel-btn">
            Annulla
          </Button>
          
          {/* Download PDF - Dropdown con due opzioni */}
          {currentQuote?.id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  disabled={saving}
                  className="border-amber-300 text-amber-600 hover:bg-amber-50"
                  data-testid="furniture-quote-download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica PDF
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => handleDownloadPDF('summary')}
                  className="cursor-pointer"
                  data-testid="furniture-quote-pdf-client-item"
                >
                  <FileText className="w-4 h-4 mr-2 text-blue-600" />
                  <div>
                    <div className="font-medium">PDF Cliente</div>
                    <div className="text-xs text-gray-500">Senza prezzi singoli</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDownloadPDF('detailed')}
                  className="cursor-pointer"
                  data-testid="furniture-quote-pdf-full-item"
                >
                  <FileCheck className="w-4 h-4 mr-2 text-green-600" />
                  <div>
                    <div className="font-medium">PDF Completo</div>
                    <div className="text-xs text-gray-500">Con tutti i dettagli</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Salva (resta aperto nella modale) */}
          <Button 
            variant="outline" 
            onClick={() => handleSave(false, true)} 
            disabled={saving}
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
            data-testid="furniture-quote-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
          
          {/* Salva e chiudi */}
          <Button 
            variant="outline" 
            onClick={() => handleSave(false, false)} 
            disabled={saving}
            className="border-purple-500 text-purple-700 bg-purple-50 hover:bg-purple-100"
            data-testid="furniture-quote-save-and-close-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva e chiudi'}
          </Button>
          
          {/* Presenta al Cliente - chiude sempre */}
          <Button 
            onClick={() => handleSave(true, false)} 
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="furniture-quote-present-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Presenta al Cliente'}
          </Button>
        </DialogFooter>

        {/* Dialog Voci Frequenti */}
        <Dialog open={showFrequentItems} onOpenChange={setShowFrequentItems}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-600" />
                Voci Frequenti
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {frequentItems.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  Nessuna voce frequente salvata.<br />
                  <span className="text-sm">Salva le voci dal preventivo per riutilizzarle.</span>
                </p>
              ) : (
                frequentItems.map((item) => (
                  <div 
                    key={item.id}
                    onClick={() => handleAddFrequentItem(item)}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                  >
                    <p className="font-medium text-sm">{item.description}</p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(item.default_unit_price)} × {item.default_quantity}
                      {item.category && ` • ${item.category}`}
                    </p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Salva come Voce Frequente */}
        <Dialog open={showSaveFrequent} onOpenChange={setShowSaveFrequent}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Salva come Voce Frequente</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-sm text-gray-600">
                "{itemToSaveAsFrequent?.description}"
              </p>
              <div>
                <Label className="text-sm">Categoria (opzionale)</Label>
                <Select 
                  value={newFrequentItem.category} 
                  onValueChange={(v) => setNewFrequentItem(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cucina">Cucina</SelectItem>
                    <SelectItem value="Bagno">Bagno</SelectItem>
                    <SelectItem value="Camera">Camera</SelectItem>
                    <SelectItem value="Living">Living</SelectItem>
                    <SelectItem value="Altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaveFrequent(false)}>
                Annulla
              </Button>
              <Button onClick={handleSaveAsFrequent} className="bg-purple-600 hover:bg-purple-700 text-white">
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default FurnitureQuoteForm;
